import React from 'react';
import _ from 'lodash';
import pickFp from 'lodash/fp/pick';
import pipeFp from 'lodash/fp/pipe';
import reduceFp from 'lodash/fp/reduce';
import {
  getId,
  getProductType,
} from '@nerdwallet/marketplace/selectors/common';
import { getLegacyId } from '@nerdwallet/marketplace/selectors/credit-cards';
import { PRODUCT_TYPES } from '@nerdwallet/marketplace/constants';
import getTextColor from './text-color-config';

export const isNullable = (value) => value === null || value === undefined;

// an immutable alternative to set, with two main differences:
// - the data object has to be provided in a thunk
// - instead of a value, it accepts a function that will receive the value in
//    the specified path and the full object as a second parameter
// ideal to use in promises and/or piped functions
export const atPath = (path, fn) => (data) =>
  _.set(_.cloneDeep(data), path, fn(_.get(data, path), data));

export const pickMultiple = (keyPathDict) => (source) =>
  _.mapValues(keyPathDict, (path) => _.get(source, path));

export const formatTable = {
  currency: (x) => (typeof x === 'number' ? `$${x}` : x),
  percent: (x) => (typeof x === 'number' ? `${x}%` : x),
  months: (x) => (typeof x === 'number' ? `${x} Months` : x),
};

// map-card-data helpers
export const dangerouslySetInnerHTML = (text) => (
  // eslint-disable-next-line react/no-danger -- directive added automatically by Shepherd migration
  <span dangerouslySetInnerHTML={{ __html: text }} />
);

export const calculateCreditScore = (maxFico, minFico) => {
  const min = {
    300: 'Poor',
    630: 'Average',
    690: 'Good',
    720: 'Excellent',
  };
  const max = {
    629: 'Poor',
    689: 'Average',
    719: 'Good',
    850: 'Excellent',
  };
  return `${min[minFico] || ''} - ${max[maxFico] || ''}`;
};

// Removes null and undefined and empty string properties from an object.
// this does not mutate the original object
export const withoutEmptyValues = (input) =>
  _.reduce(
    input,
    (acc, value, key) =>
      isNullable(value) || value === '' ? acc : _.set(acc, key, value),
    {}
  );

/* Offer API helpers */

// format values based on the value/type style coming from the offer API
export const format = ({ value, type }) => {
  const formatter = formatTable[type] || _.identity;
  return formatter(value);
};

// wrapper for lodash.get with format
export const getTypedValue = (obj, path, fallback = '') =>
  format(_.get(obj, path, {})) || fallback;

const human2fico = (rating) =>
  _.get(
    {
      poor: [300, 629],
      average: [630, 689],
      good: [690, 719],
      excellent: [720, 850],
    },
    rating
  );

const filterIfPresent = (value, predicate) =>
  isNullable(value) ? true : predicate(value);

export const validateOffer = (queryParams, adminCategoryPage) => (offer) => {
  if (!offer) {
    return false;
  }
  // QueryFilters from form
  const scoreParam = _.get(queryParams, 'credit_score');
  const institutionParam = _.get(queryParams, 'financial_institution');
  const networkParam = _.get(queryParams, 'card_network');
  const annualFeeParam = _.get(queryParams, 'no_annual_fee');

  // Conditions
  const compareInstitution = (institution) =>
    institution === _.get(offer, 'product.institution.name');
  const compareNetwork = (network) =>
    network === _.get(offer, 'details.processing_network.value');
  const compareCreditLimits = ([min, max]) =>
    min >= _.get(offer, 'details.min_fico.value') &&
    max <= _.get(offer, 'details.max_fico.value');
  const noAnnualFee = (fee) =>
    fee === 'true' ? _.get(offer, 'details.annual_fee.value') === 0 : true;
  const bannedCategory = (tableCategory) =>
    !_.get(offer, 'details.banned_pages.value', []).includes(tableCategory);

  // make the matches only if the filters have been defined in the GET params
  // or in table config; otherwise, the card is considered valid

  return (
    filterIfPresent(institutionParam, compareInstitution) &&
    filterIfPresent(networkParam, compareNetwork) &&
    filterIfPresent(scoreParam, pipeFp(human2fico, compareCreditLimits)) &&
    filterIfPresent(annualFeeParam, noAnnualFee) &&
    filterIfPresent(adminCategoryPage, bannedCategory)
  );
};

export const getHeaders = (req, list) =>
  list.reduce(
    (headers, header) => ({
      ...headers,
      [header]: req.get(header),
    }),
    {}
  );

// Takes in an array and two indexes, then it swaps the position of the element on those indices
// @param {Array} list          An array of any shape
// @param {Integer} oldIndex    The index of the current position of the element you want to move
// @param {Integer} newIndex    The target index
// @return {Array}              Reordered array
//
// @example
//  moveItem(['a', 'b', 'c'], 2, 0) // => ['c', 'b', 'a']
export const moveItem = (list, oldIndex, newIndex) => {
  const newList = [...list.slice(0, oldIndex), ...list.slice(oldIndex + 1)];

  return [
    ...newList.slice(0, newIndex),
    list[oldIndex],
    ...newList.slice(newIndex),
  ];
};

export const getProps = pickFp;

// Config builder checkboxes hold either [] (unchecked) or ['', 'some value'] (checked).
// Extract the value if present and cast to boolean.
export const valueFromConfigBuilderCheckbox = (value) =>
  Boolean(_.get(value, 1));

// checks against the URL for the presence of /l/builder
export const isInBuilder = (url = '') => url.search(/\/l\/builder/) !== -1;

export const offerCondition = (field, type, value) => ({ field, type, value });

export const activeOfferQuery = [
  offerCondition('offer.status', 'eq', 'ACTIVE'),
  offerCondition('offer.product.status', 'eq', 'ACTIVE'),
  offerCondition('offer.product.institution.status', 'eq', 'ACTIVE'),
];

export const unpackArrayIds = reduceFp(
  (accum, el) => [...accum, typeof el === 'object' ? el.id : el],
  []
);

export const unpackStringIds = (ids = '') =>
  ids
    .split(/,\s+/g)
    .filter((e) => !!e)
    .reduce((list, entry) => list.concat(parseInt(entry, 10)), []);

export const unpackDataDependencyIds = (dataDependency) =>
  typeof dataDependency?.data?.ids === 'string'
    ? unpackStringIds(dataDependency?.data?.ids)
    : unpackArrayIds(dataDependency?.data?.ids);

export const getOfferById = (id, offers, useCCLegacyIds = true) => {
  let predicate = (marketplaceEntity) => getId(marketplaceEntity) === id;

  // credit cards is a snowflake that still sometimes uses legacy IDs
  if (
    useCCLegacyIds &&
    getProductType(_.get(offers, 0)) === PRODUCT_TYPES.CREDIT_CARDS
  ) {
    predicate = (marketplaceEntity) =>
      getLegacyId(marketplaceEntity) === parseInt(id, 10);
  }

  return _.find(offers, predicate);
};

export const getOffersByIds = ({ ids = [], offers, useCCLegacyIds }) =>
  ids.map((id) => getOfferById(id, offers, useCCLegacyIds)).filter(Boolean);

export const getTextColorStyle = (textColorOptions, propertyName = 'color') => {
  const { textColor: color } = getTextColor(textColorOptions);
  return { [propertyName]: color };
};
